/**
 * ESS Climate Intelligence — Info Note
 * Shared "ⓘ what does this mean?" trigger + popover, used throughout the
 * site wherever a data value needs a plain-language explanation:
 * DATA → WHAT IT IS → WHAT THE VALUE MEANS → AGRICULTURAL IMPLICATION → TAKEAWAY
 */

const INFO_ICON = `<svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
  <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
</svg>`;

/**
 * Renders the small "ⓘ" trigger button. `id` must match the `id` passed to
 * the corresponding infoPanel() call, and must be unique across the page.
 */
export function infoTrigger(id, opts = {}) {
  const label = opts.label || '';
  return `<button type="button" class="info-trigger" data-info-target="${id}" aria-label="${label || 'What does this mean?'}">
    ${INFO_ICON}${label ? `<span>${label}</span>` : ''}
  </button>`;
}

/**
 * Renders the (initially hidden) explanatory popover.
 * opts:
 *   title       — panel heading
 *   define      — [{ term, text }] glossary entries ("What is El Niño?")
 *   meaning     — "What does this value mean?" paragraph
 *   implication — "Agricultural implication" paragraph
 *   takeaway    — "User takeaway" paragraph (visually emphasized)
 *   source      — small attribution line
 */
export function infoPanel(id, opts = {}) {
  const { title, define, meaning, implication, takeaway, source } = opts;
  return `
    <div class="info-panel" id="info-${id}" role="dialog" aria-label="${title || 'What does this mean?'}">
      <div class="info-panel__inner">
        <button type="button" class="info-panel__close" data-info-close aria-label="Close">&times;</button>
        ${title ? `<div class="info-panel__title">${title}</div>` : ''}
        ${define && define.length ? `
          <div class="info-panel__define">
            ${define.map(d => `
              <div class="info-panel__term">
                <strong>${d.term}</strong>
                <p>${d.text}</p>
              </div>
            `).join('')}
          </div>
        ` : ''}
        ${meaning ? `
          <div class="info-panel__block">
            <div class="info-panel__label">What does this mean?</div>
            <p data-role="meaning">${meaning}</p>
          </div>
        ` : ''}
        ${implication ? `
          <div class="info-panel__block">
            <div class="info-panel__label">Agricultural implication</div>
            <p>${implication}</p>
          </div>
        ` : ''}
        ${takeaway ? `
          <div class="info-panel__block info-panel__block--takeaway">
            <div class="info-panel__label">Takeaway</div>
            <p>${takeaway}</p>
          </div>
        ` : ''}
        ${source ? `<div class="info-panel__source source-note">${source}</div>` : ''}
      </div>
    </div>
  `;
}

/**
 * Updates a panel's "What does this mean?" paragraph after live data
 * arrives (the panel markup is written before the async fetch resolves).
 */
export function setInfoMeaning(id, html) {
  const el = document.querySelector(`#info-${id} [data-role="meaning"]`);
  if (el) el.innerHTML = html;
}

let activePanel = null;

/**
 * Wires up delegated click/scroll/escape handling for every .info-panel,
 * including ones rendered later (async re-renders). Call once at bootstrap.
 */
export function initInfoNotes() {
  injectInfoNoteStyles();

  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-info-close]');
    if (closeBtn) { closePanel(); return; }

    const trigger = e.target.closest('.info-trigger');
    if (trigger) {
      const id = trigger.dataset.infoTarget;
      const panel = document.getElementById(`info-${id}`);
      if (!panel) return;
      const reopening = panel !== activePanel;
      closePanel();
      if (reopening) openPanel(trigger, panel);
      return;
    }

    if (activePanel && !e.target.closest('.info-panel')) closePanel();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePanel();
  });

  // Closing on scroll is meant to dismiss the panel when the user scrolls
  // the page away from it — not when they scroll the panel's OWN content.
  // This listener is capture-phase on window so it can see the page scroll
  // from anywhere, but scroll events don't bubble — a capture listener on
  // window still fires for a descendant's own scroll (e.g. the panel's
  // internal overflow-y: auto), so without the target check below, reading
  // past the first screenful of a long panel would close it on the very
  // scroll gesture meant to reveal more of it.
  // Separately, opening a panel can itself trigger a page scroll a few ms
  // later (html has scroll-behavior: smooth, and focusing the clicked
  // button can scroll it into view) — the grace period absorbs that so the
  // panel doesn't close the instant it appears.
  window.addEventListener('scroll', (e) => {
    if (activePanel && e.target && activePanel.contains(e.target)) return;
    if (Date.now() - lastOpenedAt < SCROLL_CLOSE_GRACE_MS) return;
    closePanel();
  }, { passive: true, capture: true });
  window.addEventListener('resize', () => closePanel());
}

const SCROLL_CLOSE_GRACE_MS = 400;
let lastOpenedAt = 0;

function openPanel(trigger, panel) {
  // Portal to <body> lazily (on first open) so `position: fixed` isn't
  // affected by transformed ancestors (e.g. .anim-on-scroll's transform,
  // which creates a new containing block for descendant fixed elements).
  // Panels added to the DOM after initInfoNotes() ran (async re-renders
  // like crop/province dashboards) still get portaled correctly this way.
  if (panel.parentElement !== document.body) document.body.appendChild(panel);

  panel.classList.add('open');
  activePanel = panel;
  lastOpenedAt = Date.now();
  positionPanel(trigger, panel);
  trigger.classList.add('info-trigger--active');
}

function closePanel() {
  if (!activePanel) return;
  activePanel.classList.remove('open');
  document.querySelectorAll('.info-trigger--active').forEach(t => t.classList.remove('info-trigger--active'));
  activePanel = null;
}

function positionPanel(trigger, panel) {
  const rect = trigger.getBoundingClientRect();
  const panelWidth = Math.min(360, window.innerWidth - 32);
  panel.style.width = `${panelWidth}px`;

  let left = rect.left;
  if (left + panelWidth > window.innerWidth - 16) left = window.innerWidth - panelWidth - 16;
  left = Math.max(16, left);

  const estHeight = Math.min(panel.scrollHeight || 280, window.innerHeight * 0.7);
  // Never render under/over the fixed 60px site nav (z-index 500) — the
  // panel is itself z-index 1000 so it would otherwise sit on top of the
  // nav bar and obscure it when a near-top trigger (e.g. a Hero card) opens.
  const NAV_HEIGHT = 68;
  let top = rect.bottom + 10;
  if (top + estHeight > window.innerHeight - 16) top = rect.top - estHeight - 10;
  top = Math.max(NAV_HEIGHT, Math.min(top, window.innerHeight - 16));

  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;
}

function injectInfoNoteStyles() {
  if (document.getElementById('info-note-styles')) return;
  const style = document.createElement('style');
  style.id = 'info-note-styles';
  style.textContent = `
    .info-trigger {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      color: var(--clr-text-tertiary);
      padding: 3px;
      border-radius: var(--radius-full);
      transition: color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
      vertical-align: middle;
      line-height: 0;
    }
    .info-trigger:hover, .info-trigger--active {
      color: var(--clr-accent);
      background: var(--clr-accent-glow);
    }
    .info-trigger span {
      font-size: var(--text-xs);
      font-weight: var(--weight-medium);
      line-height: 1.2;
    }

    .info-panel {
      position: fixed;
      display: none;
      /* Above the hardcoded z-index:400 used by map legends/info-boxes
         (which sit in the same root stacking context once portaled to
         <body>), but below the full-page modal. */
      z-index: var(--z-overlay);
      background: var(--clr-surface-3);
      border: 1px solid var(--clr-border-glow);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-xl);
      backdrop-filter: var(--glass-blur);
      -webkit-backdrop-filter: var(--glass-blur);
      max-height: 70vh;
      overflow-y: auto;
    }
    .info-panel.open {
      display: block;
      animation: scaleIn var(--dur-fast) var(--ease-spring);
    }

    .info-panel__inner { padding: var(--space-5); position: relative; }

    .info-panel__close {
      position: absolute;
      top: var(--space-3);
      right: var(--space-3);
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius-full);
      color: var(--clr-text-tertiary);
      font-size: var(--text-lg);
      line-height: 1;
    }
    .info-panel__close:hover { background: var(--clr-surface-2); color: var(--clr-text-primary); }

    .info-panel__title {
      font-family: var(--font-display);
      font-weight: var(--weight-bold);
      font-size: var(--text-base);
      color: var(--clr-text-primary);
      margin-bottom: var(--space-3);
      padding-right: var(--space-6);
    }

    .info-panel__define {
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
      margin-bottom: var(--space-3);
    }

    .info-panel__term strong {
      display: block;
      font-size: var(--text-xs);
      font-family: var(--font-mono);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--clr-accent);
      margin-bottom: 2px;
    }

    .info-panel__term p,
    .info-panel__block p {
      font-size: var(--text-sm);
      color: var(--clr-text-secondary);
      line-height: 1.6;
    }

    .info-panel__block { margin-bottom: var(--space-3); }
    .info-panel__block:last-child { margin-bottom: 0; }

    .info-panel__label {
      font-size: 10px;
      font-weight: var(--weight-semi);
      text-transform: uppercase;
      letter-spacing: 0.07em;
      color: var(--clr-text-tertiary);
      font-family: var(--font-mono);
      margin-bottom: 2px;
    }

    .info-panel__block--takeaway {
      background: rgba(141,197,61,0.06);
      border: 1px solid rgba(141,197,61,0.15);
      border-radius: var(--radius-md);
      padding: var(--space-3);
    }
    .info-panel__block--takeaway .info-panel__label { color: var(--clr-accent); }

    .info-panel__source {
      margin-top: var(--space-3);
      padding-top: var(--space-3);
      border-top: 1px solid var(--clr-border);
    }

    .info-scale {
      display: inline-block;
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      color: var(--clr-text-secondary);
      letter-spacing: 0.02em;
    }

    @media (max-width: 640px) {
      .info-panel { left: 16px !important; right: 16px; width: auto !important; }
    }
  `;
  document.head.appendChild(style);
}
